// src/features/feeds/components/FeedsPage.tsx

import { useMemo, useCallback, useRef, useState, useEffect } from 'react';
import { Stack, Title, Text, Loader, Alert } from '@mantine/core';
import { useDebouncedValue, useHotkeys } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useNavigate } from '@tanstack/react-router';
import { IconAlertCircle } from '@tabler/icons-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useFeeds } from '../useFeeds';
import {
  useActivateFeeds,
  useDeactivateFeeds,
  useDeleteFeeds,
  useCreateFeed,
} from '../useFeedMutations';
import { useFeedGridEngine } from '../hooks/useFeedGridEngine';
import { useFeedGridState } from '../stores/feedGridState';
import { useSelection } from '@/shared/contexts/SelectionContext';
import { FeedUtils } from '../FeedUtils';
import { FeedGrid } from './FeedGrid';
import { FeedToolbar } from './FeedToolbar';
import { CreateFeedModal } from './CreateFeedModal';
import { FeedPublishModal } from './FeedPublishModal';
import { FEED_SEARCH_DEBOUNCE_MS } from '@/lib/constants/feed';
import { markEnd, markStart } from '@/shared/utils/perf';
import type { FeedData, FeedGridRow, FeedSortKey } from '@/lib/types/Feed';

function FeedsPageContent() {
  const { selectedCId, selectedCustomer } = useAuth();
  const navigate = useNavigate();
  const { feeds, isLoading, isError, error } = useFeeds();
  const { selectedIds, clearSelection } = useSelection();

  const searchText = useFeedGridState((s) => s.searchText);
  const setSearchText = useFeedGridState((s) => s.setSearchText);
  const searchMode = useFeedGridState((s) => s.searchMode);
  const setSearchMode = useFeedGridState((s) => s.setSearchMode);
  const sortKey = useFeedGridState((s) => s.sortKey);
  const sortDirection = useFeedGridState((s) => s.sortDirection);
  const setSort = useFeedGridState((s) => s.setSort);
  const setScrollTop = useFeedGridState((s) => s.setScrollTop);
  const awaitingBackNavPaint = useFeedGridState((s) => s.awaitingBackNavPaint);
  const completeBackNavPaint = useFeedGridState((s) => s.completeBackNavPaint);

  const activateMutation = useActivateFeeds();
  const deactivateMutation = useDeactivateFeeds();
  const deleteMutation = useDeleteFeeds();
  const createMutation = useCreateFeed();

  const [publishFeed, setPublishFeed] = useState<FeedData | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const initialScrollTopRef = useRef(useFeedGridState.getState().scrollTop);
  const hasReportedFirstRowsPaintRef = useRef(false);
  const pendingSortPaintRef = useRef(false);

  // Route guard: redirect if feed entitlement is missing
  const allowFeed = selectedCustomer?.cfg.quota.features.account.allowFeed !== false;
  useEffect(() => {
    if (selectedCustomer && !allowFeed) {
      navigate({ to: '/videos' });
    }
  }, [selectedCustomer, allowFeed, navigate]);

  useEffect(() => {
    markStart('feeds.grid.firstRowsPaint');
  }, []);

  useHotkeys([
    ['Escape', () => clearSelection()],
  ]);

  const [debouncedSearchText] = useDebouncedValue(searchText, FEED_SEARCH_DEBOUNCE_MS);

  const gridEngine = useFeedGridEngine({
    customerId: selectedCId,
    feeds,
    sort: { key: sortKey, direction: sortDirection },
    search: { mode: searchMode, text: debouncedSearchText },
  });

  const feedsById = useMemo(
    () => new Map(feeds.map((f) => [f.feedId, f])),
    [feeds],
  );

  const handleSort = useCallback(
    (key: FeedSortKey) => {
      pendingSortPaintRef.current = true;
      markStart('feeds.grid.sort.total');
      setSort(key);
    },
    [setSort],
  );

  // --- Bulk action handlers ---

  const handleBulkActivate = useCallback(() => {
    const ids = Array.from(selectedIds);
    activateMutation.mutate(ids, {
      onSuccess: () => {
        notifications.show({ title: 'Activated', message: `${ids.length} feed(s) activated.`, color: 'green' });
        clearSelection();
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  }, [selectedIds, activateMutation, clearSelection]);

  const handleBulkDeactivate = useCallback(() => {
    const ids = Array.from(selectedIds);
    deactivateMutation.mutate(ids, {
      onSuccess: () => {
        notifications.show({ title: 'Deactivated', message: `${ids.length} feed(s) deactivated.`, color: 'green' });
        clearSelection();
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  }, [selectedIds, deactivateMutation, clearSelection]);

  const handleBulkDelete = useCallback(() => {
    const ids = Array.from(selectedIds);
    modals.openConfirmModal({
      title: 'Delete Feeds',
      children: (
        <Text size="sm">
          Are you sure you want to delete {ids.length} feed(s)? This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteMutation.mutate(ids, {
          onSuccess: () => {
            notifications.show({ title: 'Deleted', message: `${ids.length} feed(s) deleted.`, color: 'green' });
            clearSelection();
          },
          onError: (err) => {
            notifications.show({ title: 'Error', message: err.message, color: 'red' });
          },
        });
      },
    });
  }, [selectedIds, deleteMutation, clearSelection]);

  // --- Single-row action handlers ---

  const handlePublish = useCallback((gridRow: FeedGridRow) => {
    const fullFeed = feedsById.get(gridRow.feedId);
    if (fullFeed) setPublishFeed(fullFeed);
  }, [feedsById]);

  const handleRowActivateToggle = useCallback((feed: FeedGridRow) => {
    const mutation = feed.isActive ? deactivateMutation : activateMutation;
    const action = feed.isActive ? 'Deactivated' : 'Activated';
    mutation.mutate([feed.feedId], {
      onSuccess: () => {
        notifications.show({ title: action, message: `"${feed.title}" ${action.toLowerCase()}.`, color: 'green' });
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  }, [activateMutation, deactivateMutation]);

  const handleRowDelete = useCallback((feed: FeedGridRow) => {
    deleteMutation.mutate([feed.feedId], {
      onSuccess: () => {
        notifications.show({ title: 'Deleted', message: `"${feed.title}" deleted.`, color: 'green' });
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  }, [deleteMutation]);

  const handleCreateFeed = useCallback((title: string) => {
    const req = FeedUtils.createNewFeedRequest(title);
    createMutation.mutate(req, {
      onSuccess: (created) => {
        setCreateModalOpen(false);
        const displayId = FeedUtils.getDisplayId(created.feedId);
        notifications.show({ title: 'Created', message: `"${title}" created.`, color: 'green' });
        navigate({ to: `/feeds/${displayId}` });
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  }, [createMutation, navigate]);

  const handleFirstRowsPaint = useCallback(() => {
    if (!hasReportedFirstRowsPaintRef.current) {
      hasReportedFirstRowsPaintRef.current = true;
      markEnd('feeds.grid.firstRowsPaint', { count: gridEngine.count });
    }

    if (pendingSortPaintRef.current) {
      pendingSortPaintRef.current = false;
      markEnd('feeds.grid.sort.total', {
        count: gridEngine.count,
        key: sortKey,
        direction: sortDirection,
      });
    }

    if (!awaitingBackNavPaint) return;
    markEnd('feeds.grid.backnav.firstRowsPaint', { count: gridEngine.count });
    completeBackNavPaint();
  }, [awaitingBackNavPaint, completeBackNavPaint, gridEngine.count, sortDirection, sortKey]);

  if (isLoading) {
    return (
      <Stack align="center" justify="center" h={400}>
        <Loader size="lg" />
        <Text c="dimmed">Loading feeds...</Text>
      </Stack>
    );
  }

  if (isError) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
        {error?.message ?? 'Failed to load feeds'}
      </Alert>
    );
  }

  return (
    <Stack gap="md" h="100%" style={{ flex: 1, minHeight: 0 }}>
      <Title order={2}>Feeds</Title>

      <FeedToolbar
        searchText={searchText}
        onSearchChange={setSearchText}
        searchMode={searchMode}
        onSearchModeChange={setSearchMode}
        totalCount={feeds.length}
        resultCount={gridEngine.resultCount}
        onBulkActivate={handleBulkActivate}
        onBulkDeactivate={handleBulkDeactivate}
        onBulkDelete={handleBulkDelete}
        onCreateFeed={() => setCreateModalOpen(true)}
        activateLoading={activateMutation.isPending}
        deactivateLoading={deactivateMutation.isPending}
        deleteLoading={deleteMutation.isPending}
      />

      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <FeedGrid
          count={gridEngine.count}
          allIds={gridEngine.orderedIds}
          getRow={gridEngine.getRowByIndex}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          onPublish={handlePublish}
          onActivateToggle={handleRowActivateToggle}
          onDelete={handleRowDelete}
          initialScrollTop={initialScrollTopRef.current}
          onScrollTopChange={setScrollTop}
          onFirstVisibleRowsPaint={handleFirstRowsPaint}
        />
      </div>

      {publishFeed && (
        <FeedPublishModal
          opened={!!publishFeed}
          onClose={() => setPublishFeed(null)}
          feed={publishFeed}
        />
      )}

      <CreateFeedModal
        opened={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={handleCreateFeed}
        loading={createMutation.isPending}
      />
    </Stack>
  );
}

export function FeedsPage() {
  return <FeedsPageContent />;
}
